import {
  fetchMapConfig as fetchMapConfigRaw,
  fetchMapLegend as fetchMapLegendRaw,
  type MapConfig,
  type MapLegendResponse,
} from '@/lib/api/map';

const LEGEND_STORAGE_PREFIX = 'monday-gis:legend:';
const LEGEND_TTL_MS = 24 * 60 * 60 * 1000;
const DEFAULT_LEGEND_DISTRICT = 'tzafonn';

type StoredLegendPayload = {
  expiresAt: number;
  data: MapLegendResponse;
};

let configCache: MapConfig | null = null;
let configInflight: Promise<MapConfig> | null = null;

let legendCache: MapLegendResponse | null = null;
let legendInflight: Promise<MapLegendResponse> | null = null;

function legendStorageKey(districtId: string): string {
  return `${LEGEND_STORAGE_PREFIX}${districtId}`;
}

function readLegendFromSessionStorage(
  districtId = DEFAULT_LEGEND_DISTRICT,
): MapLegendResponse | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(legendStorageKey(districtId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredLegendPayload;
    if (parsed.expiresAt <= Date.now()) {
      sessionStorage.removeItem(legendStorageKey(districtId));
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function writeLegendToSessionStorage(data: MapLegendResponse): void {
  if (typeof sessionStorage === 'undefined') {
    return;
  }
  try {
    const payload: StoredLegendPayload = {
      expiresAt: Date.now() + LEGEND_TTL_MS,
      data,
    };
    sessionStorage.setItem(
      legendStorageKey(data.districtId || DEFAULT_LEGEND_DISTRICT),
      JSON.stringify(payload),
    );
  } catch {
    /* quota / private mode */
  }
}

function hydrateLegendCache(data: MapLegendResponse): MapLegendResponse {
  legendCache = data;
  writeLegendToSessionStorage(data);
  return data;
}

/** Returns cached config synchronously when already loaded. */
export function peekMapConfig(): MapConfig | null {
  return configCache;
}

/** Warm the config cache — safe to call multiple times. */
export function prefetchMapConfig(): void {
  void loadMapConfig().catch(() => {
    /* surfaced by consumers */
  });
}

export function loadMapConfig(): Promise<MapConfig> {
  if (configCache) {
    return Promise.resolve(configCache);
  }
  if (!configInflight) {
    configInflight = fetchMapConfigRaw()
      .then((config) => {
        configCache = config;
        return config;
      })
      .finally(() => {
        configInflight = null;
      });
  }
  return configInflight;
}

export function peekMapLegend(): MapLegendResponse | null {
  if (legendCache) {
    return legendCache;
  }
  return readLegendFromSessionStorage();
}

/** Warm legend cache (map screen) — memory then sessionStorage then network. */
export function prefetchMapLegend(): void {
  void loadMapLegend().catch(() => {
    /* surfaced by MapLegend */
  });
}

export function loadMapLegend(): Promise<MapLegendResponse> {
  if (legendCache) {
    return Promise.resolve(legendCache);
  }

  const fromSession = readLegendFromSessionStorage();
  if (fromSession) {
    legendCache = fromSession;
    return Promise.resolve(fromSession);
  }

  if (!legendInflight) {
    legendInflight = fetchMapLegendRaw()
      .then((data) => hydrateLegendCache(data))
      .finally(() => {
        legendInflight = null;
      });
  }
  return legendInflight;
}

/** Defer non-critical work until the browser is idle. */
export function runWhenIdle(task: () => void, timeoutMs = 1500): () => void {
  if (typeof requestIdleCallback !== 'undefined') {
    const id = requestIdleCallback(task, { timeout: timeoutMs });
    return () => cancelIdleCallback(id);
  }
  const id = window.setTimeout(task, 80);
  return () => window.clearTimeout(id);
}
